<template>
  <GChart
    :settings="{packages: ['corechart']}"
    :version="'42'"
    :createChart="createChart"
    class="base-bar-chart"
  />
</template>

<script>
import { GChart } from 'vue-google-charts'
import _ from 'lodash'
import { mapState } from 'vuex'
// eslint-disable-next-line no-unused-vars
import BarChart from '../features/reports/common/BarChart'

const barColors = {
  FIRST_DATE_COLOR: '#1d3bdd',
  SECOND_DATE_COLOR: '#77d781',
  VOICE_COLOR: '#4d9de0'
}

const BAR_HEIGHT = 35
/**
 * Base Bar Chart component for RankZone tabs used in {@link BarChart}
 */
export default {
  name: 'BaseBarChart',
  components: {
    GChart
  },
  mounted () {
    window.addEventListener('resize', this.resizeListener)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.resizeListener)
  },
  props: {
    list1: {
      type: Array,
      required: true
    },
    list2: {
      type: Array
    },
    date1: {
      type: String,
      default: new Date().toISOString()
    },
    date2: {
      type: String
    },
    highlightFirst: Boolean
  },
  watch: {
    list1 () {
      const value = this.prepareDateByType()
      this.chartData = { data: value.data, colorSchema: value.colorSchema }
      this.createChart(this.el, this.google)
    },
    list2 () {
      const value = this.prepareDateByType()
      this.chartData = { data: value.data, colorSchema: value.colorSchema }
      this.createChart(this.el, this.google)
    },
    isNavbarCollapsed () {
      this.resizeListener()
    }
  },
  data () {
    return {
      chartData: this.prepareDateByType(),
      chart: null,
      google: null,
      el: null,
      debounced: _.throttle(() => {
        this.createChart(this.el, this.google)
      }, 100, { leading: true, trailing: true })
    }
  },
  computed: {
    ...mapState([
      'isNavbarCollapsed'
    ])
  },
  methods: {
    resizeListener () {
      this.debounced.flush()
      this.debounced()
    },
    /**
     * Preparing chart data according to
     * {@link https://developers.google.com/chart/interactive/docs/datatables_dataviews#arraytodatatable}
     *
     * @returns {chartData:  {data: array of chart data, colorSchema: array of colors} }
     */
    prepareDateByType: function () {
      const header = [ 'Company', this.date1 ]
      if (this.date2 && this.list2.length) {
        header.push(this.date2)
      }
      const result = []
      const source = this.list1.length ? this.list1 : this.list2
      source.forEach((item, index) => {
        const name = this.list1[index] ? this.list1[index].name : this.list2[index].name
        const value1 = this.list1[index] ? this.list1[index].value : 0
        const line = [name, value1]

        if (this.list2 && this.list2.length) {
          const value2 = this.list2[index] ? this.list2[index].value : 0
          line.push(value2)
        }
        result.push(line)
      })
      result.unshift(header)
      return {
        data: result,
        colorSchema: Object.values(barColors).slice(0, result.length)
      }
    },
    /**
     * Callback for createChart property of vue-google-charts library
     * all list of available options {@see https://developers.google.com/chart/interactive/docs/gallery/barchart#configuration-options}
     * prepared data passed into chat using DataView {@link https://developers.google.com/chart/interactive/docs/datatables_dataviews}
     * @param el - chart html element
     * @param google - link to the Google Chart library
     */
    createChart (el, google) {
      if (!google) {
        return
      }
      if (!this.google) {
        this.google = google
        this.el = el
      }
      if (this.chart) {
        this.chart.clearChart()
      }
      const data = google.visualization.arrayToDataTable(this.chartData.data)
      const length = this.chartData.data.length
      const height = length * BAR_HEIGHT
      const view = new google.visualization.DataView(data)
      const options = {
        colors: this.chartData.colorSchema,
        legend: 'none',
        backgroundColor: 'transparent',
        height,
        width: '100%',
        // title: 'none',
        chartArea: { width: '65%', height: '70%', left: '30%', top: 0 },
        label: 'none',
        tooltip: { trigger: 'focus' },
        bars: 'horizontal',
        fontName: 'Roboto',
        fontSize: 12,
        bar: { groupWidth: '90%' },
        hAxis: {
          format: 'short',
          gridlines: {
            count: 10,
            color: '#b2b9be'
          },
          textStyle: {
            color: '#b2b9be',
            fontSize: 10
          }
        },
        vAxis: {
          textStyle: {
            color: '#011627',
            fontSize: 12,
            fontWeight: 500,
            bold: true
          }
        }
      }
      this.chart = new google.visualization.BarChart(el)
      google.visualization.events.removeAllListeners(this.chart)

      google.visualization.events.addListener(this.chart, 'select', () => {
        this.chart.setSelection(null)
      })
      // we need it to prevent appearance errors on the screen
      google.visualization.events.addListener(this.chart, 'error', function (err) {
        google.visualization.errors.removeError(err.id)
      })

      // hightlight first y-axis label
      google.visualization.events.addListener(this.chart, 'ready', () => {
        if (!this.highlightFirst) {
          return
        }
        try {
          const firstCompanyElement = this.findFirstLabel()
          if (firstCompanyElement) {
            firstCompanyElement.style[ 'font-weight' ] = 'bold'
            firstCompanyElement.setAttribute('private-content', true)
          }
        } catch (e) {
          console.log(e)
        }
      })

      this.chart.draw(view, options)
    },
    findFirstLabel () {
      const elements = Array.prototype.slice.apply(
        this.el.querySelectorAll(`text[text-anchor="end"]`)
      )
      const topElement = elements.reduce((min, current) => {
        return Number(current.getAttribute('y')) < Number(min.getAttribute('y')) ? current : min
      }, elements[0])
      return topElement
    }
  }
}

</script>

<style scoped lang="scss">

</style>
